import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, DollarSign, TrendingUp, Activity } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import type { Deal, Contact, DashboardStats as DashboardStatsType } from "@shared/schema";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const STAGE_COLORS: Record<string, string> = {
    lead: "hsl(var(--chart-1))",
    qualified: "hsl(var(--chart-2))",
    proposal: "hsl(var(--chart-4))",
    negotiation: "hsl(var(--chart-5))",
    won: "hsl(var(--chart-3))",
    lost: "hsl(var(--muted))",
};

export default function DashboardPage() {
    const { data: stats, isLoading } = useQuery<DashboardStatsType>({
        queryKey: ["/api/analytics/dashboard"],
    });

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const statCards = [
        {
            title: "Total Contacts",
            value: stats?.totalContacts || 0,
            icon: Users,
            color: "text-chart-1",
        },
        {
            title: "Active Deals",
            value: stats?.totalDeals || 0,
            icon: DollarSign,
            color: "text-chart-2",
        },
        {
            title: "Pipeline Value",
            value: formatCurrency(stats?.totalValue || 0),
            icon: TrendingUp,
            color: "text-chart-3",
        },
        {
            title: "Won Deals",
            value: stats?.wonDeals || 0,
            icon: Activity,
            color: "text-chart-4",
        },
    ];

    return (
        <div className="space-y-8">
            <div>
                <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
                <p className="text-muted-foreground mt-2">Overview of your sales pipeline and key metrics</p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {statCards.map((card) => (
                    <Card key={card.title}>
                        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
                            <card.icon className={`h-4 w-4 ${card.color}`} />
                        </CardHeader>
                        <CardContent>
                            {isLoading ? (
                                <Skeleton className="h-8 w-24" />
                            ) : (
                                <div className="text-2xl font-bold" data-testid={`stat-${card.title.toLowerCase().replace(/\s+/g, "-")}`}>
                                    {card.value}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>

            <div className="grid gap-6 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>Deals by Stage</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {isLoading ? (
                            <Skeleton className="h-[300px] w-full" />
                        ) : stats?.dealsByStage && stats.dealsByStage.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={stats.dealsByStage}>
                                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                    <XAxis dataKey="stage" className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                                    <YAxis className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: "6px" }} labelStyle={{ color: "hsl(var(--popover-foreground))" }} />
                                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-[300px] flex items-center justify-center text-muted-foreground">No data available</div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Pipeline Distribution</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {isLoading ? (
                            <Skeleton className="h-[300px] w-full" />
                        ) : stats?.dealsByStage && stats.dealsByStage.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie data={stats.dealsByStage} dataKey="value" nameKey="stage" cx="50%" cy="50%" outerRadius={80} label={(entry: any) => `${entry.stage}: ${formatCurrency(entry.value)}`}>
                                        {stats.dealsByStage.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={STAGE_COLORS[entry.stage] || "hsl(var(--muted))"} />
                                        ))}
                                    </Pie>
                                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: "6px" }} formatter={(value: number) => formatCurrency(value)} />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-[300px] flex items-center justify-center text-muted-foreground">No data available</div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
